import { memo, useCallback, useEffect, useRef, type FunctionComponent, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import Alert from '@mui/material/Alert';
// local imports
import useIsNarrowScreen from '../hooks/useIsNarrowScreen';
import CardSection from '../elements/CardSection';

type DesktopOnlyProps = {
  readonly children?: ReactNode;
  // for Storybook only
  readonly testCase?: number;
};

const DesktopOnly: FunctionComponent<DesktopOnlyProps> = ({
  children,
  testCase
}) => {
  const isNarrow = useIsNarrowScreen();
  const narrow = isNarrow || testCase === 1;

  const noticeRef = useRef<HTMLDivElement>(null);
  const childrenRef = useRef<HTMLDivElement>(null);
  const focusWasInside = useRef(false);
  // Seeded with the mounted value, so a component that *started* narrow never counts as a swap.
  const wasNarrow = useRef(narrow);

  const handleFocus = useCallback(() => { focusWasInside.current = true; }, []);
  // Removing the focused node fires no `focusout`, so this only clears on a real move away.
  const handleBlur = useCallback(() => { focusWasInside.current = false; }, []);

  useEffect(() => {
    if (wasNarrow.current === narrow) return;
    wasNarrow.current = narrow;
    if (focusWasInside.current) (narrow ? noticeRef : childrenRef).current?.focus();
  }, [narrow]);

  return narrow ? (
    <div ref={noticeRef} tabIndex={-1} onFocus={handleFocus} onBlur={handleBlur}>
      <CardSection>
        <Alert severity="info" variant="standard">
          <FormattedMessage id="common.desktop_only"/>
        </Alert>
      </CardSection>
    </div>
  ) : (
    <div ref={childrenRef} tabIndex={-1} onFocus={handleFocus} onBlur={handleBlur}>
      {children}
    </div>
  );
};

export default memo(DesktopOnly);
